import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';
import { Plus, X, Search, User as UserIcon, Trash2, Edit2, Shield, Users, UserCheck, ChevronDown, ChevronRight } from 'lucide-react';

interface DepartmentGroup {
  id: string;
  name: string;
  departments: string[];
}

interface UserMapping {
  id: string;
  name: string;
  email: string;
  role: 'LEADER' | 'COMMITTEE_HEAD' | 'COMMITTEE_MEMBER' | 'EXTERNAL_COMMITTEE' | 'EXECUTION_COMMITTEE' | 'MIS_COMMITTEE';
  managedDepartments: string[];
  reportsToId?: string | null;
  subCoreRole?: 'COST_CENTRE_MEMBER' | 'COORDINATOR' | 'DATA_ANALYST' | 'EXECUTOR' | 'COST_ACCOUNTANT' | null;
}

interface Employee {
  id: string;
  name: string;
  email: string;
  department: string | null;
  cgId?: string | null;
}

export function DepartmentMappingsV2() {
  const queryClient = useQueryClient();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState<DepartmentGroup | null>(null);
  const [editingUser, setEditingUser] = useState<UserMapping | null>(null);
  const [formRole, setFormRole] = useState<'LEADER' | 'COMMITTEE_HEAD' | 'COMMITTEE_MEMBER' | 'EXTERNAL_COMMITTEE' | 'EXECUTION_COMMITTEE' | 'MIS_COMMITTEE'>('COMMITTEE_MEMBER');
  const [formReportsTo, setFormReportsTo] = useState<string>('');
  const [formSubCore, setFormSubCore] = useState<'COST_CENTRE_MEMBER' | 'COORDINATOR' | 'DATA_ANALYST' | 'EXECUTOR' | 'COST_ACCOUNTANT' | ''>('');
  const [formError, setFormError] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string, departmentName: string, user: UserMapping } | null>(null);

  // Employee Search State
  const [empSearch, setEmpSearch] = useState('');
  const [isEmpDropdownOpen, setIsEmpDropdownOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState<{ name: string; email: string } | null>(null);

  // Queries
  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['department-groups'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/department-groups');
      return res.data.data as DepartmentGroup[];
    }
  });

  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['userRoleMappings'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/user-role-mappings');
      return res.data.data as UserMapping[];
    }
  });

  const { data: employeesData } = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/employees');
      return res.data.data as Employee[];
    }
  });

  const groups = groupsData || [];
  const users = usersData || [];
  const employees = employeesData || [];

  // Mutations
  const updateMutation = useMutation({
    mutationFn: async (payload: { name: string; email: string; role: string; departments: string[]; reportsToId?: string; subCoreRole?: string }) => {
      return apiClient.post('/admin/user-role-mappings', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userRoleMappings'] });
      closeModal();
    },
    onError: (err: any) => {
      setFormError(err.response?.data?.error?.message || 'Failed to save mapping');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (payload: { id: string, departmentName: string }) => {
      return apiClient.delete(`/admin/user-role-mappings/${payload.id}?department=${encodeURIComponent(payload.departmentName)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userRoleMappings'] });
    }
  });

  // Derived filtered users per group
  const getUsersByGroup = (groupName: string) => {
    return users.filter(u => u.managedDepartments.includes(groupName));
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const openModal = (group: DepartmentGroup, role: 'LEADER' | 'COMMITTEE_HEAD' | 'COMMITTEE_MEMBER', editUser?: UserMapping) => {
    setActiveGroup(group);
    setFormRole(role);
    if (editUser) {
      setEditingUser(editUser);
      setSelectedEmp({ name: editUser.name, email: editUser.email });
      setEmpSearch(`${editUser.name} (${editUser.email})`);
      let validReportsTo = '';
      if (editUser.reportsToId) {
        const managers = role === 'COMMITTEE_HEAD'
          ? users.filter(u => u.role === 'LEADER' && u.managedDepartments.includes(group.name))
          : users.filter(u => u.role === 'COMMITTEE_HEAD' && u.managedDepartments.includes(group.name));

        if (managers.some(m => m.id === editUser.reportsToId)) {
          validReportsTo = editUser.reportsToId;
        }
      }
      setFormReportsTo(validReportsTo);
      setFormSubCore(editUser.subCoreRole || '');
    } else {
      setEditingUser(null);
      setSelectedEmp(null);
      setEmpSearch('');
      setFormReportsTo('');
      setFormSubCore('');
      setFormError('');
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setActiveGroup(null);
    setEditingUser(null);
    setSelectedEmp(null);
    setEmpSearch('');
    setFormReportsTo('');
    setFormSubCore('');
    setFormError('');
    setIsEmpDropdownOpen(false);
  };

  const openModalGlobal = (role: 'EXTERNAL_COMMITTEE' | 'EXECUTION_COMMITTEE', editUser: UserMapping | null) => {
    setActiveGroup({ id: 'GLOBAL', name: 'GLOBAL', departments: [] });
    setFormRole(role);
    if (editUser) {
      setEditingUser(editUser);
      setSelectedEmp({ name: editUser.name, email: editUser.email });
      setEmpSearch(`${editUser.name} (${editUser.email})`);
    } else {
      setEditingUser(null);
      setSelectedEmp(null);
      setEmpSearch('');
    }
    setFormReportsTo('');
    setFormSubCore('');
    setFormError('');
    setIsModalOpen(true);
  };

  const openModalMis = (editUser: UserMapping | null) => {
    setActiveGroup({ id: 'MIS', name: 'MIS Committee', departments: [] });
    setFormRole('MIS_COMMITTEE');
    if (editUser) {
      setEditingUser(editUser);
      setSelectedEmp({ name: editUser.name, email: editUser.email });
      setEmpSearch(`${editUser.name} (${editUser.email})`);
    } else {
      setEditingUser(null);
      setSelectedEmp(null);
      setEmpSearch('');
    }
    setFormReportsTo('');
    setFormSubCore('');
    setFormError('');
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!selectedEmp || !activeGroup) {
      setFormError('Please select an employee.');
      return;
    }
    if (formRole !== 'LEADER' && formRole !== 'EXTERNAL_COMMITTEE' && formRole !== 'EXECUTION_COMMITTEE' && formRole !== 'MIS_COMMITTEE' && !formReportsTo) {
      setFormError('Please select a manager to report to.');
      return;
    }

    setFormError('');

    const existingMapped = users.find(
      (u) => u.email.toLowerCase().trim() === selectedEmp.email.toLowerCase().trim()
    );
    const lockedRoles = new Set([
      'LEADER',
      'COMMITTEE_HEAD',
      'COMMITTEE_MEMBER',
      'EXTERNAL_COMMITTEE',
      'EXECUTION_COMMITTEE',
      'MIS_COMMITTEE',
    ]);
    if (existingMapped && lockedRoles.has(existingMapped.role) && existingMapped.role !== formRole) {
      setFormError(
        `${existingMapped.name} is already mapped as ${existingMapped.role.replace(/_/g, ' ')}. They can only be added or updated with the same role.`
      );
      return;
    }
    if (
      existingMapped &&
      existingMapped.role === 'COMMITTEE_MEMBER' &&
      formRole === 'COMMITTEE_MEMBER' &&
      existingMapped.subCoreRole &&
      formSubCore &&
      existingMapped.subCoreRole !== formSubCore
    ) {
      setFormError(
        `${existingMapped.name} is already a Committee Member with sub-role ${existingMapped.subCoreRole.replace(/_/g, ' ')}. Sub-role mismatch. Use the same sub-role.`
      );
      return;
    }

    const resolvedSubCore =
      existingMapped?.role === 'COMMITTEE_MEMBER' && existingMapped.subCoreRole
        ? existingMapped.subCoreRole
        : formSubCore;

    if (formRole === 'COMMITTEE_MEMBER' && !resolvedSubCore) {
      setFormError('Please select a Sub-Core Role for this member.');
      return;
    }

    const isStandaloneRole = formRole === 'EXTERNAL_COMMITTEE' || formRole === 'EXECUTION_COMMITTEE' || formRole === 'MIS_COMMITTEE';
    const payload = {
      name: selectedEmp.name,
      email: selectedEmp.email,
      role: formRole,
      departments: (activeGroup.name === 'GLOBAL' || formRole === 'MIS_COMMITTEE') ? [] : [activeGroup.name],
      reportsToId: (formRole === 'LEADER' || isStandaloneRole) ? undefined : formReportsTo,
      subCoreRole: formRole === 'COMMITTEE_MEMBER' ? resolvedSubCore : undefined
    };

    if (editingUser && editingUser.email !== selectedEmp.email) {
      // If employee changed, delete the old mapping first
      const departmentKey = (activeGroup.name === 'GLOBAL' || formRole === 'MIS_COMMITTEE') ? 'GLOBAL' : activeGroup.name;
      apiClient.delete(`/admin/user-role-mappings/${editingUser.id}?department=${encodeURIComponent(departmentKey)}`).then(() => {
        updateMutation.mutate(payload);
      });
    } else {
      updateMutation.mutate(payload);
    }
  };

  const handleDelete = (id: string, departmentName: string) => {
    const user = users.find(u => u.id === id);
    if (user) {
      setDeleteConfirm({ id, departmentName, user });
    }
  };

  const filteredEmployees = useMemo(() => {
    if (!empSearch) return employees.slice(0, 50);
    const lowerSearch = empSearch.toLowerCase();
    return employees.filter(e =>
      (e.name || '').toLowerCase().includes(lowerSearch) ||
      (e.email || '').toLowerCase().includes(lowerSearch) ||
      (e.cgId || '').toLowerCase().includes(lowerSearch)
    ).slice(0, 50);
  }, [employees, empSearch]);

  const renderHierarchy = (group: DepartmentGroup) => {
    const groupUsers = getUsersByGroup(group.name);
    const leaders = groupUsers.filter(u => u.role === 'LEADER');
    const heads = groupUsers.filter(u => u.role === 'COMMITTEE_HEAD');
    const members = groupUsers.filter(u => u.role === 'COMMITTEE_MEMBER');

    return (
      <div className="p-6 bg-slate-50/50 space-y-8">

        {/* LEADER SECTION */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider flex items-center gap-2">
              <Shield className="w-4 h-4 text-indigo-600" /> Group Leaders
            </h4>
            <button
              onClick={() => openModal(group, 'LEADER')}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add Leader
            </button>
          </div>

          {leaders.length > 0 ? (
            <div className="space-y-3">
              {leaders.map(leader => (
                <div key={leader.id} className="bg-white border border-indigo-100 rounded-xl p-4 shadow-sm flex items-center justify-between group/user">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-lg">
                      {leader.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold text-slate-800">{leader.name}</div>
                      <div className="text-xs text-slate-500">{leader.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover/user:opacity-100 transition-opacity">
                    <button onClick={() => openModal(group, 'LEADER', leader)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(leader.id, group.name)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
              <p className="text-sm font-medium text-slate-500 mb-2">No Leader assigned for this group.</p>
              <button onClick={() => openModal(group, 'LEADER')} className="text-sm font-bold text-indigo-600 hover:underline">
                Assign a Leader now
              </button>
            </div>
          )}
        </div>

        {/* COMMITTEE HEADS SECTION */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-emerald-600" /> Committee Heads
            </h4>
            <button
              onClick={() => openModal(group, 'COMMITTEE_HEAD')}
              className="text-xs font-bold text-emerald-600 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add Head
            </button>
          </div>

          {heads.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {heads.map(head => {
                const manager = leaders.find(l => l.id === head.reportsToId);
                return (
                  <div key={head.id} className="bg-white border border-emerald-100 rounded-xl p-4 shadow-sm flex items-center justify-between group/user">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-lg">
                        {head.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-bold text-slate-800">{head.name}</div>
                        <div className="text-xs text-slate-500">{head.email}</div>
                        <div className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider">
                          Reports to: <span className="text-slate-600">{manager?.name || 'Unknown'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover/user:opacity-100 transition-opacity">
                      <button onClick={() => openModal(group, 'COMMITTEE_HEAD', head)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(head.id, group.name)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="border border-slate-200 bg-slate-50 rounded-xl p-4 text-center">
              <p className="text-xs font-medium text-slate-500">No Committee Heads assigned.</p>
            </div>
          )}
        </div>

        {/* COMMITTEE MEMBERS SECTION */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" /> Committee Members
            </h4>
            <button
              onClick={() => openModal(group, 'COMMITTEE_MEMBER')}
              className="text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add Member
            </button>
          </div>

          {members.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {members.map(member => {
                const manager = heads.find(h => h.id === member.reportsToId);
                return (
                  <div key={member.id} className="bg-white border border-blue-100 rounded-xl p-4 shadow-sm flex items-center justify-between group/user">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
                        {member.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-bold text-slate-800 text-sm">{member.name}</div>
                        <div className="text-[11px] text-slate-500">{member.email}</div>
                        <div className="text-[10px] font-bold text-slate-400 mt-0.5 uppercase tracking-wider flex items-center gap-1.5 flex-wrap">
                          <span>Reports to: <span className="text-slate-600">{manager?.name || 'Unknown'}</span></span>
                          {member.subCoreRole && (
                            <span className="bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded text-[9px] font-black">
                              {member.subCoreRole.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover/user:opacity-100 transition-opacity">
                      <button onClick={() => openModal(group, 'COMMITTEE_MEMBER', member)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button onClick={() => handleDelete(member.id, group.name)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="border border-slate-200 bg-slate-50 rounded-xl p-4 text-center">
              <p className="text-xs font-medium text-slate-500">No Committee Members assigned.</p>
            </div>
          )}
        </div>

      </div>
    );
  };

  const renderGlobalCommittee = (role: 'EXTERNAL_COMMITTEE' | 'EXECUTION_COMMITTEE' | 'MIS_COMMITTEE', title: string, subtitle = 'Global Approval Committee') => {
    const assignedUser = users.find(u => u.role === role);
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden transition-all duration-200">
        <div className="px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-4">
            <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-800">{title}</h3>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                {subtitle} • {assignedUser ? '1 assigned member' : 'No assigned member'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {assignedUser ? (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-600 text-xs">
                    {assignedUser.name.charAt(0)}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-bold text-slate-800">{assignedUser.name}</div>
                    <div className="text-xs text-slate-500">{assignedUser.email}</div>
                  </div>
                </div>
                <button
                  onClick={() => role === 'MIS_COMMITTEE' ? openModalMis(assignedUser) : openModalGlobal(role, assignedUser)}
                  className="ml-4 px-4 py-2 bg-slate-100 text-slate-600 hover:bg-slate-200 font-bold text-xs rounded-xl transition-colors flex items-center gap-2"
                >
                  <Edit2 className="w-3.5 h-3.5" /> Update Member
                </button>
              </div>
            ) : (
              <button
                onClick={() => role === 'MIS_COMMITTEE' ? openModalMis(null) : openModalGlobal(role, null)}
                className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 font-bold text-xs rounded-xl transition-colors"
              >
                Assign Member
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout title="Committee Mappings v2">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight font-display">Committee Assignments</h1>
          <p className="text-sm text-slate-500 mt-1">Assign Leaders, Heads, and Members to Department Groups.</p>
        </div>

        {isLoadingGroups || isLoadingUsers ? (
          <div className="flex items-center justify-center py-20 text-indigo-600 font-bold animate-pulse">
            Loading Hierarchy...
          </div>
        ) : (
          <div className="space-y-6">
            {/* Global Committees */}
            <div className="space-y-4">
              <h2 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-500" /> Global Committees
              </h2>
              {renderGlobalCommittee('EXTERNAL_COMMITTEE', 'External Committee')}
              {/* {renderGlobalCommittee('EXECUTION_COMMITTEE', 'Execution Committee')} */}
            </div>

            <div className="h-px bg-slate-200 w-full my-6"></div>

            {/* Department Groups */}
            <div className="space-y-4">
              <h2 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" /> Committee Groups
              </h2>
              {groups.map(group => {
                const isExpanded = expandedGroups[group.id];
                const groupUsers = getUsersByGroup(group.name);

                return (
                  <div key={group.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden transition-all duration-200">
                    <div
                      onClick={() => toggleGroup(group.id)}
                      className="px-6 py-5 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg transition-transform duration-200 ${isExpanded ? 'rotate-90 bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                          <ChevronRight className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-black text-slate-800">{group.name}</h3>
                          <p className="text-xs text-slate-500 font-medium mt-0.5">
                            {group.departments.length} underlying departments • {groupUsers.length} assigned members
                          </p>
                        </div>
                      </div>
                      <div className="flex -space-x-2 overflow-hidden px-2">
                        {groupUsers.slice(0, 5).map(u => (
                          <div key={u.id} className="flex-shrink-0 h-8 w-8 rounded-full ring-2 ring-white bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600" title={u.name}>
                            {u.name.charAt(0)}
                          </div>
                        ))}
                        {groupUsers.length > 5 && (
                          <div className="flex-shrink-0 h-8 w-8 rounded-full ring-2 ring-white bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">
                            +{groupUsers.length - 5}
                          </div>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-slate-100 animate-in slide-in-from-top-2 duration-200">
                        {renderHierarchy(group)}
                      </div>
                    )}
                  </div>
                );
              })}

              {groups.length === 0 && (
                <div className="text-center py-20 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <p className="text-slate-500 font-medium">No Department Groups found. Create groups in the Department Groupings page first.</p>
                </div>
              )}
            </div>

            <div className="h-px bg-slate-200 w-full my-6"></div>

            <div className="space-y-4">
              <h2 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" /> MIS Committee
              </h2>
              {renderGlobalCommittee('MIS_COMMITTEE', 'MIS Committee', 'One MIS user')}
            </div>
          </div>
        )}
      </div>

      {/* Assignment Modal */}
      {isModalOpen && activeGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <h2 className="text-lg font-black text-slate-800 font-display">
                {formRole === 'MIS_COMMITTEE'
                  ? (editingUser ? 'Update MIS Committee' : 'Assign MIS Committee')
                  : (editingUser ? 'Edit Assignment' : `Add ${formRole.replace('_', ' ').toLowerCase()} to ${activeGroup.name}`)}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4 text-red-500 flex-shrink-0" />
                  {formError}
                </div>
              )}

              {/* Employee Selection */}
              <div>
                <label className="block text-xs uppercase font-bold text-slate-600 tracking-wider mb-2">Employee</label>
                <div className="relative">
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search employee by name or email..."
                      className="w-full pl-9 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      value={empSearch}
                      onChange={(e) => {
                        setEmpSearch(e.target.value);
                        setIsEmpDropdownOpen(true);
                      }}
                      onFocus={() => setIsEmpDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setIsEmpDropdownOpen(false), 200)}
                    />
                  </div>
                  {isEmpDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {filteredEmployees.length > 0 ? (
                        filteredEmployees.map(emp => (
                          <div
                            key={emp.id}
                            onClick={() => {
                              setSelectedEmp({ name: emp.name, email: emp.email });
                              setEmpSearch(`${emp.name} (${emp.email})`);
                              setIsEmpDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0"
                          >
                            <div className="font-bold text-sm text-slate-800">{emp.name}</div>
                            <div className="text-xs text-slate-500">
                              {emp.email}
                              {emp.cgId && <span className="ml-2 px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-bold">EMP ID: {emp.cgId}</span>}
                              {emp.department && <span className="ml-2 px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[10px] font-bold">{emp.department}</span>}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-slate-500">No employees found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Role Selection (Read-only since it's determined by which button they clicked) */}
              <div>
                <label className="block text-xs uppercase font-bold text-slate-600 tracking-wider mb-2">Assigned Role</label>
                <div className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 flex items-center justify-between">
                  <span>{formRole.replace('_', ' ')}</span>
                  <span className="text-xs font-bold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">{activeGroup.name}</span>
                </div>
              </div>

              {/* Reporting To Selection */}
              {(formRole !== 'LEADER' && formRole !== 'EXTERNAL_COMMITTEE' && formRole !== 'EXECUTION_COMMITTEE' && formRole !== 'MIS_COMMITTEE') && (
                <div>
                  <label className="block text-xs uppercase font-bold text-slate-600 tracking-wider mb-2">
                    Reports To ({formRole === 'COMMITTEE_HEAD' ? 'Leader' : 'Committee Head'})
                  </label>
                  <div className="relative">
                    <select
                      className="w-full appearance-none pl-4 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      value={formReportsTo}
                      onChange={(e) => setFormReportsTo(e.target.value)}
                    >
                      <option value="" disabled>-- Select Manager --</option>
                      {formRole === 'COMMITTEE_HEAD'
                        ? getUsersByGroup(activeGroup.name).filter(u => u.role === 'LEADER').map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))
                        : getUsersByGroup(activeGroup.name).filter(u => u.role === 'COMMITTEE_HEAD').map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))
                      }
                    </select>
                    <ChevronDown className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
              )}

              {/* Sub-Core Role Selection (Only for Committee Members) */}
              {formRole === 'COMMITTEE_MEMBER' && (
                <div>
                  <label className="block text-xs uppercase font-bold text-slate-600 tracking-wider mb-2">
                    Sub-Core Role
                  </label>
                  <div className="relative">
                    <select
                      className="w-full appearance-none pl-4 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      value={formSubCore}
                      onChange={(e) => setFormSubCore(e.target.value as any)}
                    >
                      <option value="" disabled>-- Select Sub-Core Role --</option>
                      <option value="COST_CENTRE_MEMBER">Cost Centre Member</option>
                      <option value="COORDINATOR">Coordinator</option>
                      <option value="DATA_ANALYST">Data Analyst</option>
                      <option value="EXECUTOR">Executor</option>
                      <option value="COST_ACCOUNTANT">Cost Accountant</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  {formSubCore && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-2">
                      <Shield className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-800 leading-relaxed">
                        <strong>Warning:</strong> Changing this Sub-Core Role will apply to <strong>all</strong> committee groups this member belongs to. Their role will be updated globally.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-5 py-2.5 text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:text-slate-800 transition-colors"
                disabled={updateMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedEmp || updateMutation.isPending || ((formRole === 'COMMITTEE_HEAD' || formRole === 'COMMITTEE_MEMBER') && !formReportsTo) || (formRole === 'COMMITTEE_MEMBER' && !formSubCore)}
                className="px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Mapping'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3 bg-red-50/50">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-800 font-display">Remove Member</h2>
                <p className="text-sm text-slate-500 font-medium">Please confirm your action</p>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-slate-600 text-sm leading-relaxed">
                Are you sure you want to remove <span className="font-bold text-slate-800">{deleteConfirm.user.name}</span> from <span className="font-bold text-slate-800">{deleteConfirm.departmentName}</span>?
              </p>

              {deleteConfirm.user.role === 'COMMITTEE_HEAD' && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1 text-sm">
                    <p className="font-bold text-amber-900">Warning: Impact on Members</p>
                    <p className="text-amber-800 leading-relaxed">
                      Deleting this Committee Head will leave their assigned Committee Members without a reporting head (they will report to 'UNKNOWN').
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-5 py-2.5 text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:text-slate-800 transition-colors"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteMutation.mutate({ id: deleteConfirm.id, departmentName: deleteConfirm.departmentName });
                  setDeleteConfirm(null);
                }}
                disabled={deleteMutation.isPending}
                className="px-5 py-2.5 text-sm font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors shadow-sm flex items-center gap-2"
              >
                {deleteMutation.isPending ? 'Removing...' : 'Yes, Remove Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
